import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Skeleton, SkeletonText } from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import {
  findMarketingContentEntryByRoute,
  MarketingFrontMatter,
  loadMarketingPageMarkdown,
  MarketingContentIndexEntry,
  MarketingPageTemplate,
  parseMarketingFrontMatter,
} from "../../marketing-content/marketingContentLoader";
import { trackErrorWhenLoaded as trackError } from "../../utils/analytics/track-error-when-loaded";
import { MarketingPageMain, MarketingSiteShell } from "./MarketingSiteShell";
import { MarketingSeoHead } from "./MarketingSeoHead";
import { MarketingArticlePage } from "./MarketingArticlePage";
import { MarketingLandingTemplatePage } from "./MarketingLandingTemplatePage";
import { BlogIndexPage } from "./BlogIndexPage";
import { MarketingPricingPage } from "./MarketingPricingPage";
import { MarketingNotFoundPage } from "./MarketingNotFoundPage";
import { MarketingLoadFailure } from "./MarketingLoadFailure";
import { MarketingSiteLoadingFallback } from "./MarketingSiteLoadingFallback";
import { getPrerenderedMarketingPageHtml } from "./marketingSiteRoutes";
import { MarketingTemplatePageProps } from "./marketingTemplatePageProps";
import { takePrimedMarketingPageMarkdown } from "./marketingPageMarkdownCache";

type LoadedMarketingPage = { frontMatter: MarketingFrontMatter; body: string; renderedBodyHtml: string | null };

// Pages already fetched and parsed in this session, so returning to one is instant and the page
// tree renders from the cache without a loading state.
const loadedMarketingPagesByRoute = new Map<string, LoadedMarketingPage>();

/**
 * Fetches the entry's Markdown chunk (once per route) and returns it parsed. While it loads the
 * result is `null`; a failed fetch is reported and returned as `didLoadFail` so the page can say so
 * instead of showing a skeleton forever. Failures are not cached, so a later visit retries.
 */
function useLoadedMarketingPage(entry: MarketingContentIndexEntry | null): {
  loadedPage: LoadedMarketingPage | null;
  didLoadFail: boolean;
} {
  const [, rerenderAfterLoad] = useReducer((renderCount: number) => renderCount + 1, 0);
  const [failedLoadRoute, setFailedLoadRoute] = useState<string | null>(null);

  // Text the build (or the generated page) provided up front needs no fetch: it is parsed right here so
  // this very render is the complete page.
  if (entry && !loadedMarketingPagesByRoute.has(entry.route)) {
    const primed = takePrimedMarketingPageMarkdown(entry.route);
    if (primed) {
      loadedMarketingPagesByRoute.set(entry.route, {
        ...parseMarketingFrontMatter(primed.markdownFileText),
        renderedBodyHtml: primed.renderedBodyHtml,
      });
    }
  }

  useEffect(() => {
    if (!entry || loadedMarketingPagesByRoute.has(entry.route)) return;
    let isThisRouteStillShown = true;
    loadMarketingPageMarkdown(entry)
      .then((markdownFileText) => {
        loadedMarketingPagesByRoute.set(entry.route, {
          ...parseMarketingFrontMatter(markdownFileText),
          renderedBodyHtml: null,
        });
        // A retry after an earlier failure (visitor left and came back) clears that failure.
        setFailedLoadRoute((currentFailedLoadRoute) =>
          currentFailedLoadRoute === entry.route ? null : currentFailedLoadRoute
        );
        if (isThisRouteStillShown) rerenderAfterLoad();
      })
      .catch((error) => {
        trackError(error, { scope: "marketing-page-markdown-load", extra: { route: entry.route } });
        if (isThisRouteStillShown) setFailedLoadRoute(entry.route);
      });
    return () => {
      isThisRouteStillShown = false;
    };
  }, [entry]);

  if (!entry) return { loadedPage: null, didLoadFail: false };
  return {
    loadedPage: loadedMarketingPagesByRoute.get(entry.route) ?? null,
    didLoadFail: failedLoadRoute === entry.route,
  };
}

const MarketingPageSkeleton = React.memo(function MarketingPageSkeleton({
  template,
}: {
  template: MarketingPageTemplate;
}) {
  return (
    <MarketingPageMain template={template}>
      <article className="marketing-article" aria-busy="true">
        <Skeleton h="44px" w="80%" borderRadius="8px" mb="28px" />
        <SkeletonText noOfLines={6} spacing="14px" skeletonHeight="14px" />
      </article>
    </MarketingPageMain>
  );
});

const MarketingTemplatePage = React.memo(function MarketingTemplatePage(templatePageProps: MarketingTemplatePageProps) {
  switch (templatePageProps.entry.template) {
    case "landing":
      return <MarketingLandingTemplatePage {...templatePageProps} />;
    case "blogIndex":
      return <BlogIndexPage {...templatePageProps} />;
    case "pricing":
      return <MarketingPricingPage {...templatePageProps} />;
    case "article":
    default:
      return <MarketingArticlePage {...templatePageProps} />;
  }
});

/**
 * The route element for every marketing sub-page and blog post (registered in AppRouter from
 * src/marketing-content/index.json). It looks the page up by path, loads its Markdown and renders
 * the page's template inside the marketing shell; an unknown path shows the "page not found" screen.
 */
export default function MarketingContentPage() {
  const { pathname } = useLocation();
  const entry = useMemo(() => findMarketingContentEntryByRoute(pathname), [pathname]);
  const { loadedPage, didLoadFail } = useLoadedMarketingPage(entry);

  // In-app navigation keeps the scroll position; a new page should start at the top. Not on the first
  // render: the static HTML is on screen long before this runs, and the visitor may have scrolled.
  const isFirstPathnameRef = useRef(true);
  useEffect(() => {
    if (isFirstPathnameRef.current) {
      isFirstPathnameRef.current = false;
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname]);

  if (!entry) return <MarketingNotFoundPage pathname={pathname} />;
  if (didLoadFail) return <MarketingLoadFailure language={entry.lang} />;
  // A page served as build-time static HTML keeps showing that HTML (it is this very page) rather than a
  // skeleton while its Markdown chunk downloads; the head tags it came with stay until Helmet takes over.
  if (!loadedPage && getPrerenderedMarketingPageHtml()) return <MarketingSiteLoadingFallback />;

  return (
    <MarketingSiteShell lang={entry.lang}>
      <MarketingSeoHead entry={entry} />
      {loadedPage ? (
        <MarketingTemplatePage
          entry={entry}
          frontMatter={loadedPage.frontMatter}
          markdownBody={loadedPage.body}
          renderedBodyHtml={loadedPage.renderedBodyHtml}
        />
      ) : (
        <MarketingPageSkeleton template={entry.template} />
      )}
    </MarketingSiteShell>
  );
}
